import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from 'components/ui/Box';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, InputGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';
import {
  INVENTORY_UPLOAD_TYPE_OPTIONS,
  XLSX_ONLY_UPLOAD_TYPES,
} from 'constants/inventory-2/uploads';

import { formSchema } from './form-schema';
import type { FormSchema, UploadType } from './form-schema';

export type UploadDataPayload = {
  uploadType: UploadType;
  file: File;
}

type Props = {
  onSubmit: (data: UploadDataPayload) => void;
  loading?: boolean;
};

export const UploadDataForm = ({ onSubmit, loading }: Props) => {
  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      uploadType: INVENTORY_UPLOAD_TYPE_OPTIONS[0].value,
    },
  });

  const uploadType = form.watch('uploadType');
  const xlsxOnly = XLSX_ONLY_UPLOAD_TYPES.includes(uploadType);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    onSubmit({
      file: formData.file[0],
      uploadType: formData.uploadType,
    });
  }, [onSubmit]);

  return (
    <Box className={'w-full'}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onFormSubmit)}
          className="flex flex-col gap-2 pb-2"
        >
          <div className="flex flex-row gap-4">
            <div className={'w-full grid grid-cols-2 gap-4'}>
              <FormField
                control={form.control}
                name={'uploadType'}
                render={({ field }) => (
                  <ComboboxGs
                    form={form}
                    fieldName={field.name}
                    fieldValue={field.value}
                    content={INVENTORY_UPLOAD_TYPE_OPTIONS}
                    label={'Tipo de planilha'}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'file'}
                render={() => (
                  <InputGs
                    type={'file'}
                    label={'Arquivo'}
                    accept={xlsxOnly ? '.xlsx' : '.csv,.xlsx'}
                    {...form.register('file')}
                  />
                )}
              />
            </div>
            <div className={'mt-[25px] w-40 shrink-0'}>
              <Button.Root className={'w-full'} type={'submit'} size={'sm'} disabled={loading}>
                {loading ? <ActivityIndicator /> : 'Enviar planilha'}
              </Button.Root>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            {xlsxOnly
              ? 'Formato aceito: .xlsx, até 50 MB.'
              : 'Formato aceito: .csv ou .xlsx, até 50 MB.'}
          </p>
        </form>
      </Form>
    </Box>
  );
};
